import React, { useCallback, useState } from "react";
import {
  and,
  ControlProps,
  EnumCellProps,
  EnumOption,
  formatIs,
  RankedTester,
  rankWith,
  WithClassname,
} from "@jsonforms/core";
import {
  withJsonFormsOneOfEnumCellProps,
  withTranslateProps,
} from "@jsonforms/react";
import { WithOptionLabel } from "@jsonforms/material-renderers";
import { Constants, debounce, searchICD10 } from "hhn-commons";
import { JsonFormsAutocomplete } from "./common/JsonFormsAutocomplete";

const formatAsListLabel = (valueJson: string) => {
  const parsedData = JSON.parse(valueJson);
  return `${parsedData.value} - ${parsedData.label}`;
};

export const ICD10AutocompleteCell = (
  props: EnumCellProps &
    Partial<ControlProps> &
    Partial<WithClassname> &
    Partial<WithOptionLabel>,
) => {
  const { required, schema, label, id, enabled, uischema, path, config } =
    props;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(props.data);

  const getInitialFilterOptions = (): {
    initialOptions: EnumOption[];
    initialOption?: EnumOption;
  } => {
    if (!props.data) {
      return { initialOptions: [], initialOption: undefined };
    }

    const initialOption = {
      label: formatAsListLabel(props.data),
      value: props.data,
    };
    return { initialOptions: [initialOption], initialOption };
  };

  const { initialOptions, initialOption } = getInitialFilterOptions();

  const [filteredOptions, setFilteredOptions] =
    useState<EnumOption[]>(initialOptions);

  const handleInputChange = useCallback(async (inputValue: string) => {
    if (inputValue) {
      setLoading(true);
      const result = await searchICD10(inputValue);
      setFilteredOptions(
        result.results.map(row => ({
          label: `${row.code} - ${row.name}`,
          value: JSON.stringify({ label: row.name, value: row.code }),
        })),
      );
      setLoading(false);
    }
  }, []);

  const helperText = data ? `ICD10 Code: ${JSON.parse(data).value}` : "";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChange = useCallback(
    debounce((nextInputText: any) => {
      props.handleChange(path, nextInputText);
    }, Constants.API_DEBOUNCE_DELAY_IN_MS),
    [],
  );

  const handleChange = (_path: string, value: any) => {
    debouncedChange(value);
    setData(value);
  };

  return (
    <JsonFormsAutocomplete
      id={id}
      label={schema.title || label || ""}
      required={required}
      enabled={enabled}
      data={data}
      path={path}
      config={config}
      uischema={uischema}
      handleChange={handleChange}
      onInputChange={handleInputChange}
      options={filteredOptions}
      helperText={helperText}
      loading={loading}
      initialOption={initialOption}
      initialOptions={initialOptions}
    />
  );
};

export const icd10AutocompleteCellTester: RankedTester = rankWith(
  10,
  and(formatIs("icd10-autocomplete")),
);

export default withJsonFormsOneOfEnumCellProps(
  withTranslateProps(React.memo(ICD10AutocompleteCell)),
  false,
);
